import { createContext, forwardRef, useContext, useEffect, useState } from 'react';
import styles from './Table.module.css';
import Skeleton from '../Skeleton/Skeleton';
import { TableColumnSelector } from './TableColumnSelector';
import IconSortDirection from '../icons/IconSortDirection';
import Button from '../forms/Button/Button';
import IconSortDirectionAsc from '../icons/IconSortDirectionAsc';
import IconSortDirectionDesc from '../icons/IconSortDirectionDesc';
import { LocalStorageKeys, getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorageUtils';

interface TableBodyRow<TData> {
  rowData: TData;
  rowIndex: number;
  cellIndex: number;
  getCellData: <K extends keyof TData>(dataKey: K) => TData[K];
}
interface TableHeaderRow<TData> {
  cellIndex: number;
}
export interface TableColumn<TData> {
  title: string; //Controls the header for the column.
  dataKey?: string; //This is meant to represent a "key" in the data object used for each row of the table.
  id: string; //A unique id for each column in the table.
  sortable?: boolean; //Controls if the column should have the sort buttons in the header of each column and will use the tables default sorting functions. (DOES NOT SUPPORT NESTED DATA -> will need to use custom sort when sorting info in sellerData)
  visible?: boolean; //Used to determine if the column should be visible to the user.
  pinned?: 'left' | 'right'; //This IS different than alignment. This will make whole columns stick to the left or the rght side of the table.
  alignment?: 'left' | 'right'; //Allows user to position content in the header and body to one side of the column.
  width?: string; //This is the width of each column.
  renderRowCell?: (
    row: TableBodyRow<TData>,
    column: TableColumn<TData>,
    table: TableContext<TData>
  ) => React.ReactNode;
  renderHeaderCell?: (
    header: TableHeaderRow<TData>,
    column: TableColumn<TData>,
    table: TableContext<TData>
  ) => React.ReactNode;
  renderSkeleton?: () => React.ReactNode; // shows when loading, if you don't provide a loading render method for the whole table
}

interface TableContextTable<TData> {
  originalData: TData[];
  sortedData: TData[];
  sortedColumnId?: string;
  sortDirection?: 'asc' | 'desc';
  columnOrder: string[];
}

interface TableContext<TData = any> {
  table: TableContextTable<TData>;
  onSort: (id: string, direction: 'asc' | 'desc') => void;
  tableState: TableState;
  setTableState: (tableState: TableState) => void;
  isLoading: boolean;
  showSort: boolean;
}

export const TableContext = createContext<TableContext>({
  table: { originalData: [], sortedData: [], columnOrder: [] },
  onSort: () => { },
  setTableState: () => { },
  tableState: {} as TableState,
  isLoading: false,
  showSort: false,
});

interface TableHeadRowCellProps<TData> {
  cellIndex: number;
  column: TableColumn<TData>;
}

function TableHeadRowCell<TData>({ column, cellIndex }: TableHeadRowCellProps<TData>) {
  const tableContext = useContext(TableContext);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(tableContext.table.sortDirection);
  const handleSort = () => {
    if (column.sortable) {
      let direction = sortDirection ?? 'desc';
      if(tableContext.tableState.sortedColumnId == column.id) direction = sortDirection === 'desc' ? 'asc' : 'desc';
      setSortDirection(direction);

      if (tableContext.onSort) {
        tableContext.onSort(column.id, direction);
      }
    }
  }
  const header: TableHeaderRow<TData> = { cellIndex };
  const sortable = tableContext.showSort && column.sortable;
  return (
    <th
      scope="col"
      key={column.id}
      className={`
        ${cellIndex === 0 && column.pinned === 'left' ? styles.stickyColumn : ''}
        ${sortable ? styles.sortable : ""}
      `}
      style={{ width: column?.width ?? "" }}
      onClick={handleSort}
    >
      <div className={`
        ${styles.tableHeaderCellItem}
        ${cellIndex === 0 || column.alignment === 'left' ? styles.firstTableHeaderCellItem : ''}
      `}>
        {column.renderHeaderCell
          ? column.renderHeaderCell(header, column, tableContext)
          : <span className={styles.tableHeaderCellTitle}>{column.title}</span>}
        {sortable && (
          <Button
            className={styles.tableHeaderSortButton}
            onClick={handleSort}
            type={column.id === tableContext.table.sortedColumnId && !!sortDirection ? "primary" : "text"}
          >
            {(sortDirection === "asc" && column.id === tableContext.table.sortedColumnId) && <IconSortDirectionAsc size={18} />}
            {(column.id !== tableContext.table.sortedColumnId) && <IconSortDirection size={18} />}
            {(sortDirection === "desc" && column.id === tableContext.table.sortedColumnId) && <IconSortDirectionDesc size={18} />}
          </Button>
        )}
      </div>
    </th>
  );
};

interface TableHeadRowProps<TData> {
  columns: TableColumn<TData>[];
}
function TableHeadRow<TData>({ columns }: TableHeadRowProps<TData>) {
  return (
    <tr>
      {columns.map((column, index) => (
        <TableHeadRowCell key={column.id} column={column} cellIndex={index} />
      ))}
    </tr>
  );
};

interface TableBodyRowProps<TData> {
  rowIndex: number;
  rowData: TData;
  columns: TableColumn<TData>[];
}

function TableBodyRow<TData>({
  rowIndex,
  rowData,
  columns,
}: TableBodyRowProps<TData>) {
  return (
    <tr>
      {columns.map((column, index) => (
        <TableBodyRowCell
          key={column.id}
          rowData={rowData}
          rowIndex={rowIndex}
          cellIndex={index}
          column={column}
        />
      ))}
    </tr>
  );
};

export function getTableDataAtPath<T>(obj: T, path: string): any {
  // Split the path string into an array of keys
  const keys = path.split('.');

  // Iterate through the keys, navigating the object
  let current: any = obj;
  for (const key of keys) {
    if (current[key] === undefined) {
      return undefined; // Key not found
    }
    current = current[key];
  }

  return current;
};

interface TableBodyRowCellProps<TData> {
  rowIndex: number;
  cellIndex: number;
  rowData: TData;
  column: TableColumn<TData>;
}

function TableBodyRowCell<TData>({
  rowData,
  column,
  rowIndex,
  cellIndex,
}: TableBodyRowCellProps<TData>) {
  const tableContext = useContext(TableContext);
  const cellData = column.dataKey
    ? getTableDataAtPath(rowData, column.dataKey)
    : null;
  const row: TableBodyRow<TData> = {
    rowIndex,
    cellIndex,
    rowData,
    getCellData(dataKey) {
      return dataKey ? getTableDataAtPath(rowData, dataKey as string) : null;
    },
  };
  if (tableContext.isLoading) {
    return (
      <td className={cellIndex === 0 && column.pinned === 'left' ? styles.stickyColumn : ''}>
        {column.renderSkeleton ? column.renderSkeleton() : <Skeleton width={column.width || 'auto'} />}
      </td>
    );
  }
  return (
    <td className={cellIndex === 0 && column.pinned === 'left' ? styles.stickyColumn : ''}>
      <div
        className={`
          ${styles.bodyCellContainer}
          ${column.alignment === "left" ? styles.bodyCellLeftAligned : ''}
        `}
      >
        {column.renderRowCell
          ? column.renderRowCell(row, column, tableContext)
          : cellData}
      </div>
    </td>
  );
};

export const TableRowActionButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, children, ...restProps }, ref) => (
    <button ref={ref} className={`${styles.tableRowActionButton} ${className ? className : ''}`} {...restProps}>
      {children ? children : <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Icon">
          <path id="Vector"
            d="M8.00033 13.3327C7.63366 13.3327 7.31977 13.2021 7.05866 12.941C6.79755 12.6799 6.66699 12.366 6.66699 11.9993C6.66699 11.6327 6.79755 11.3188 7.05866 11.0577C7.31977 10.7966 7.63366 10.666 8.00033 10.666C8.36699 10.666 8.68088 10.7966 8.94199 11.0577C9.2031 11.3188 9.33366 11.6327 9.33366 11.9993C9.33366 12.366 9.2031 12.6799 8.94199 12.941C8.68088 13.2021 8.36699 13.3327 8.00033 13.3327ZM8.00033 9.33268C7.63366 9.33268 7.31977 9.20213 7.05866 8.94102C6.79755 8.6799 6.66699 8.36602 6.66699 7.99935C6.66699 7.63268 6.79755 7.31879 7.05866 7.05768C7.31977 6.79657 7.63366 6.66602 8.00033 6.66602C8.36699 6.66602 8.68088 6.79657 8.94199 7.05768C9.2031 7.31879 9.33366 7.63268 9.33366 7.99935C9.33366 8.36602 9.2031 8.6799 8.94199 8.94102C8.68088 9.20213 8.36699 9.33268 8.00033 9.33268ZM8.00033 5.33268C7.63366 5.33268 7.31977 5.20213 7.05866 4.94102C6.79755 4.6799 6.66699 4.36602 6.66699 3.99935C6.66699 3.63268 6.79755 3.31879 7.05866 3.05768C7.31977 2.79657 7.63366 2.66602 8.00033 2.66602C8.36699 2.66602 8.68088 2.79657 8.94199 3.05768C9.2031 3.31879 9.33366 3.63268 9.33366 3.99935C9.33366 4.36602 9.2031 4.6799 8.94199 4.94102C8.68088 5.20213 8.36699 5.33268 8.00033 5.33268Z" />
        </g>
      </svg>}
    </button>
  )
);
TableRowActionButton.displayName = 'TableRowActionButton';

export function getVisibleLeftPinnedColumns<TData>(columns: TableColumn<TData>[] | TableStateColumn[]) {
  return columns.filter(
    (column) => column.pinned === 'left' && column.visible !== false
  );
};

export function getVisibleRightPinnedColumns<TData>(columns: TableColumn<TData>[] | TableStateColumn[]) {
  return columns.filter(
    (column) => column.pinned === 'right' && column.visible !== false
  );
};

export function getVisibleUnpinnedColumns<TData>(columns: TableColumn<TData>[] | TableStateColumn[]) {
  return columns.filter((column) => !column.pinned && column.visible !== false);
};

interface TableProps<TData> {
  data: TData[];
  columns: TableColumn<TData>[];
  tableName?: string; //The name of the table. Used as a key when storing a state profile for this table. Which is currently ITS ONLY purpose.
  onSort?: (
    id: string,
    direction: 'asc' | 'desc',
    table: TableContextTable<TData>
  ) => void;
  showSort?: boolean;
  isLoading?: boolean;
  skeletonRows?: number;
  className?: string;
  header?: React.ReactNode;
  headerControls?: React.ReactNode;
  footer?: React.ReactNode;
  zeroState?: React.ReactNode;
  noResultsState?: React.ReactNode;
  loadingState?: React.ReactNode;
  tableContainerRef?: React.Ref<HTMLDivElement>;
  tableRef?: React.Ref<HTMLTableElement>;
  sortDirection?: 'asc' | 'desc';
  sortedColumnId?: string;
  columnControls?: boolean;
  containerClassName?: string;
  showHeader?: boolean;
}

type TableStateColumn = {
  id: string;
  visible: boolean;
  pinned: 'left' | 'right';
};

export type TableState = {
  sortedColumnId?: string; //Used to store the id of the column that the user has chosen to sort by previously.
  sortDirection?: 'asc' | 'desc'; //Used to store the sort direction of the saved column id.
  columns: TableStateColumn[]; //A subset of the TableColumn data. Used to persist the (id, visibility, pinned) values. The order of the columns in this array is IMPORTANT. It determines the order of the columns on the table itself and in the columns select dropdown. Doing this because we don't need any more than this in local storage for each column AND the functions associated with each column can't be persisted.
};

export function Table<TData>({
  data,
  columns,
  tableName = "",
  onSort: propsOnSort,
  showSort = false,
  isLoading = false,
  tableContainerRef,
  tableRef,
  className,
  skeletonRows = 10,
  header,
  headerControls,
  footer,
  zeroState,
  noResultsState,
  loadingState,
  sortDirection,
  sortedColumnId,
  columnControls = true,
  containerClassName = '',
  showHeader = true
}: TableProps<TData>) {
  const [sortedData, setSortedData] = useState<TData[]>([]);

  //In order to ensure the tables state (column order, column visibility, column pin, sort preference) is saved across refresh we need to first centralize the state. This is the "tableState".
  //Once centralized we can then upload that grouped state into a state profile in local storage.
  //When we refresh the page. We will use the state profile as the default state of the table.
  //If a state profile was not found. We will create one.
  //Any updates to the "tableState" will be reflected in the state profile.
  const [tableState, setTableState] = useState<TableState>(() => {
    const tableProfile: TableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    //If there isn't a state profile then create one, upload to local storage using the table name as the key suffix, then return profile as default state.
    if (!tableProfile || tableProfile.columns.length !== columns.length) {
      const newTableProfile: TableState = {
        sortDirection,
        sortedColumnId,
        columns: columns.map(col => {
          return {
            id: col.id,
            visible: col.visible === undefined ? true : col.visible,
            pinned: col.pinned,
          }
        }),
      };
      if (tableName) setLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`, newTableProfile);
      return newTableProfile;
    }

    return tableProfile;
  });

  //The table state columns represent the order that the columns need to be in.
  //We loop over then, then return the matching column in the raw, unordered, column list.
  //We then overwrite the visible value in the raw col with the value in our persisted col.
  //Finally we filter out col that have a falsy visible value.
  const visibleColumns: TableColumn<TData>[] = tableState.columns.map(tsCol => {
    const matchCol = columns.find(column => column.id === tsCol.id);
    matchCol.visible = tsCol.visible;
    return matchCol;
  }).filter(col => !!col.visible);

  const getSortedDataPage = () => {
    const newSortedData = [...data].sort((a, b) => {
      const columnDataKey = visibleColumns?.find(
        (col) => col.id === tableState.sortedColumnId
      )?.dataKey as string;
      const aValue = columnDataKey ? getTableDataAtPath(a, columnDataKey) : 0;
      const bValue = columnDataKey ? getTableDataAtPath(b, columnDataKey) : 0;

      if (aValue < bValue) return tableState.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return tableState.sortDirection === 'asc' ? 1 : -1;

      return 0;
    });

    return newSortedData;
  };

  const handleSort = (id: string, direction: 'asc' | 'desc') => {
    if (propsOnSort && typeof propsOnSort === 'function') {
      propsOnSort?.(id, direction, {
        originalData: data,
        sortedData: data,
        columnOrder: tableState.columns.map(c => c.id),
        sortedColumnId: id,
        sortDirection: direction,
      });
    } else {
      const sortedDataPage = getSortedDataPage();
      setSortedData(sortedDataPage);
    }
  };

  useEffect(() => {
    setTableState({
      ...tableState,
      sortDirection,
      sortedColumnId
    });
  }, [sortDirection, sortedColumnId]);

  //Listens for updates to the table state and save changes to table profile in local storage.
  useEffect(() => {
    if (tableName) setLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`, tableState);
  }, [tableState]);

  const tableData = sortedData.length > 0 ? sortedData : data;

  return (
    <TableContext.Provider
      value={{
        table: {
          originalData: data,
          sortedData: tableData,
          columnOrder: tableState.columns.map(c => c.id),
          sortedColumnId: tableState.sortedColumnId,
          sortDirection: tableState.sortDirection,
        },
        tableState,
        setTableState,
        onSort: handleSort,
        isLoading,
        showSort,
      }}
    >
      <div className={`${styles.tableContainer} ${containerClassName}`} ref={tableContainerRef}>
        <div className={styles.tableHeader}>
          {header}
          <div className={styles.tableHeaderControls}>
            {headerControls}
            {columnControls && (
              <TableColumnSelector columns={columns} />
            )}
          </div>
        </div>
        <div
          className={`
            ${styles.tableWrapper}
            ${!!className ? className : ""}
          `}
          style={{
            backgroundPosition: `${visibleColumns.length && visibleColumns[0].pinned ? visibleColumns[0].width : '0'}, right, ${visibleColumns.length && visibleColumns[0].pinned ? visibleColumns[0].width : '0'}, right`,
          }}
        >
          <table className={styles.table} ref={tableRef}>
            {showHeader && <thead className={styles.tableHead}>
              <TableHeadRow columns={visibleColumns} />
            </thead>}
            <tbody className={styles.tableBody}>
              {/* if we're loading, show the loading state for the table if we have one, otherwise render the skeletons for each row */}
              {isLoading ? loadingState ? <tr><td className={styles.noPadding} colSpan={columns.length}>{loadingState}</td></tr> : (
                Array.from({ length: skeletonRows })?.map((_, index) => (
                  <TableBodyRow key={index} rowData={{} as TData} rowIndex={index} columns={visibleColumns} />
                ))
              ) : (
                <>
                  {!!zeroState && (
                    <tr>
                      <td className={styles.noPadding} colSpan={columns.length}>
                        {zeroState}
                      </td>
                    </tr>
                  )}

                  {!!noResultsState && (
                    <tr>
                      <td className={styles.noPadding} colSpan={columns.length}>
                        {noResultsState}
                      </td>
                    </tr>
                  )}

                  {tableData?.map((rowData, index) => (
                    <TableBodyRow
                      key={index}
                      rowData={rowData}
                      rowIndex={index}
                      columns={visibleColumns}
                    />
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        {footer ? <div className={styles.tableFooter}>{footer}</div> : null}
      </div>
    </TableContext.Provider>
  );
}
